import { createRoot } from "react-dom/client";

import App from "./App";
import { registerPreloadErrorReload } from "./utils/handlePreloadError";
import { registerGrid } from "./utils/registerGrid";
import { printVersion } from "./currentVersion";
import envVariables from "./projectEnvVariables";
import reportWebVitals from "./reportWebVitals";
import "./styles.css";
import { initBrokerPortalTracking } from "./utils/tracking";

// Extend the Window interface to include printVersion
declare global {
  interface Window {
    printVersion: () => void;
    __mswWorker?: import("msw/browser").SetupWorkerApi;
  }
}

// In dev mode, forward console output to the mock server's /api/dev-log
// endpoint so browser logs appear in the terminal alongside server logs.
if (import.meta.env.DEV && envVariables.VITE_ENABLE_API_MOCKING === "true") {
  /* eslint-disable no-console */
  const consoleMethods = ["log", "warn", "error", "info"] as const;
  type ConsoleLevel = (typeof consoleMethods)[number];
  type ConsoleFn = (...a: unknown[]) => void;
  const original: Record<ConsoleLevel, ConsoleFn> = {
    log: console.log.bind(console) as ConsoleFn,
    warn: console.warn.bind(console) as ConsoleFn,
    error: console.error.bind(console) as ConsoleFn,
    info: console.info.bind(console) as ConsoleFn,
  };

  const forward =
    (level: ConsoleLevel) =>
    (...args: unknown[]) => {
      original[level](...args);
      fetch("/api/dev-log", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          level,
          args: args.map((a) => {
            if (typeof a !== "object" || a === null) return String(a);
            try {
              return JSON.stringify(a);
            } catch {
              return String(a);
            }
          }),
        }),
      }).catch(() => {});
    };

  consoleMethods.forEach((level) => {
    console[level] = forward(level);
  });
  /* eslint-enable no-console */
}

registerGrid();
registerPreloadErrorReload();
window.printVersion = printVersion;
printVersion();

// Initialize Datadog RUM and logging for the broker portal
initBrokerPortalTracking();

enableMocking().then(() => {
  const rootElement = document.getElementById("root");
  if (!rootElement) {
    throw new Error("Root element not found");
  }

  const root = createRoot(rootElement);
  root.render(<App />);
});

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();

async function enableMocking(): Promise<void> {
  if (envVariables.VITE_ENABLE_API_MOCKING !== "true") {
    return;
  }

  // Skip MSW when running under Playwright so page.route() interception works.
  if (navigator.webdriver) {
    return;
  }

  const { worker } = (await import("./mocks/browser")) as {
    worker: { start: (opts: { onUnhandledRequest: string }) => Promise<void> };
  };

  await worker.start({ onUnhandledRequest: "bypass" });
  window.__mswWorker =
    worker as unknown as import("msw/browser").SetupWorkerApi;
}
